import { ConversationSmsDirection } from '@provision/conversation-types';

export interface MessageSmsTag {
  conversationSmsSessionId: string;
  direction: string;
}

export interface TaggedMessageAttachment {
  fileName: string;
  url: string | null;
}

export interface TaggedMessage {
  conversationMessageId: string;
  createdAt: string;
  body?: string | null;
  senderChannelId?: string | null;
  attachments?: TaggedMessageAttachment[];
  /** True when the message's wire dispatch failed — the bubble shows "Not delivered". */
  dispatchFailed?: boolean;
  dispatchFailureReason?: string | null;
  smsTags: MessageSmsTag[];
}

export interface SmsSessionGroup {
  sessionId: string;
  startedAt: string;
  endedAt: string;
  messageCount: number;
  messages: TaggedMessage[];
  isOpen: boolean;
}

export function groupMessagesBySession(
  messages: TaggedMessage[],
  openSessionId: string | null,
): SmsSessionGroup[] {
  const sessionMap = new Map<string, TaggedMessage[]>();

  for (const msg of messages) {
    for (const tag of msg.smsTags) {
      const existing = sessionMap.get(tag.conversationSmsSessionId) ?? [];
      existing.push(msg);
      sessionMap.set(tag.conversationSmsSessionId, existing);
    }
  }

  const groups: SmsSessionGroup[] = [];

  for (const [sessionId, msgs] of sessionMap) {
    const sorted = [...msgs].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    groups.push({
      sessionId,
      startedAt: sorted[0].createdAt,
      endedAt: sorted[sorted.length - 1].createdAt,
      messageCount: sorted.length,
      messages: sorted,
      isOpen: sessionId === openSessionId,
    });
  }

  groups.sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());

  return groups;
}

export interface ThreadEntry {
  message: TaggedMessage;
  isOutbound: boolean;
  showHeader: boolean;
}

export function buildThreadEntries(group: SmsSessionGroup): ThreadEntry[] {
  return group.messages.map((message, index) => {
    const isOutbound = isOutboundIn(group, message);
    const prev = index > 0 ? group.messages[index - 1] : null;
    const showHeader =
      !prev ||
      isOutboundIn(group, prev) !== isOutbound ||
      prev.senderChannelId !== message.senderChannelId;

    return { message, isOutbound, showHeader };
  });
}

function isOutboundIn(group: SmsSessionGroup, message: TaggedMessage): boolean {
  const tag = message.smsTags.find((t) => t.conversationSmsSessionId === group.sessionId);
  return (tag?.direction as ConversationSmsDirection) === ConversationSmsDirection.Outgoing;
}

export function getSuppressedMessageIds(groups: SmsSessionGroup[]): Set<string> {
  const suppressed = new Set<string>();
  for (const g of groups) {
    for (const m of g.messages) {
      suppressed.add(m.conversationMessageId);
    }
  }
  return suppressed;
}
